// Avisos de la Sala para el médico (Sala CRM↔HCE, rebanada 5c): «turno cerrado automáticamente, revisar». El barrido de medianoche cerró una consulta
// que seguía en curso (pasó el turno de «En consulta» a «Atendida» sin que nadie la finalizara) y el médico debe confirmar que quedó completa. Sale en
// la página «Sala» y en el Dashboard, encima de la lista de la Sala.
//
// SIN sonido (el sonido es solo de la telemedicina). Cada aviso se queda hasta que el médico lo marca como revisado; «Abrir ficha del paciente» (lee el Encounter
// del aviso en Medplum para saber quién es) lleva a sus Notas, como «Iniciar consulta» de la Sala. El servidor entrega solo los avisos DEL MÉDICO de la sesión: aquí nunca se manda un id de médico.
// Sin avisos (o una cuenta sin Sala, 403) no se dibuja nada.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/sonner';
import { ErrorSala, fechaCortaEcuador, marcarAvisoRevisado, obtenerAvisosSala, pacienteDeEncounter, rutaHistoriaDeAviso, type AvisoSala } from '@/lib/salaHce';
import { crearUltimaPeticion } from '@/lib/ultimaPeticion';
import { useEventosSala } from '@/lib/useEventosSala';
import { claseTarjetaConPendientes, IndicadorPendiente } from './AlertaPendienteUI';

export function AvisosCierreSala(): JSX.Element | null {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [avisos, setAvisos] = useState<AvisoSala[]>([]);
  const [mensajeError, setMensajeError] = useState<string | null>(null);
  const [ocupados, setOcupados] = useState<ReadonlySet<number>>(new Set());
  const ultima = useRef(crearUltimaPeticion());
  /** Una cuenta sin Sala (director técnico de farmacia, admin del HCE) recibe 403: no se vuelve a consultar. */
  const sinSala = useRef(false);
  const enCurso = useRef(new Set<number>());

  const cargar = useCallback(async () => {
    if (sinSala.current) return;
    const peticion = ultima.current.nueva();
    try {
      const r = await obtenerAvisosSala();
      if (peticion.vigente()) setAvisos(Array.isArray(r.alertas) ? r.alertas : []);
    } catch (err) {
      if (err instanceof ErrorSala && err.status === 403) {
        sinSala.current = true;
        if (peticion.vigente()) setAvisos([]);
      }
      // Cualquier otro fallo (red, 5xx): se conserva lo último que se vio; el respaldo de 30 s reintenta.
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);
  // El barrido de medianoche publica «sala» al cerrar turnos: el aviso aparece sin recargar la página.
  useEventosSala(() => void cargar(), { tipos: ['sala'] });

  async function revisar(a: AvisoSala): Promise<void> {
    if (enCurso.current.has(a.id)) return;
    enCurso.current.add(a.id);
    setOcupados(new Set(enCurso.current));
    setMensajeError(null);
    try {
      await marcarAvisoRevisado(a.id);
      setAvisos((previos) => previos.filter((x) => x.id !== a.id));
    } catch (err) {
      // 404: el aviso ya no es de este médico o ya no existe: se vuelve a pedir la lista. Cualquier otro fallo se dice.
      if (!(err instanceof ErrorSala && err.status === 404)) setMensajeError(t('sala.avisosCierre.errorRevisar'));
    } finally {
      enCurso.current.delete(a.id);
      setOcupados(new Set(enCurso.current));
      void cargar();
    }
  }

  async function abrirHistoria(a: AvisoSala): Promise<void> {
    if (!a.encounterId) return;
    try {
      const encounter = await medplum.readResource('Encounter', a.encounterId);
      const pacienteId = pacienteDeEncounter(encounter);
      if (!pacienteId) {
        notify({ color: 'yellow', message: t('sala.avisosCierre.sinPaciente'), autoClose: 8000 });
        return;
      }
      void navigate(rutaHistoriaDeAviso(pacienteId));
    } catch {
      notify({ color: 'yellow', message: t('sala.avisosCierre.errorAbrir'), autoClose: 8000 });
    }
  }

  if (avisos.length === 0) return null;

  return (
    <section aria-labelledby="avisos-cierre-titulo" className={claseTarjetaConPendientes(avisos.length > 0)}>
      <div className="flex items-center gap-1.5">
        {avisos.length > 0 && <IndicadorPendiente />}
        <h2 id="avisos-cierre-titulo" className="text-sm font-medium text-hce-ink">
          {t('sala.avisosCierre.titulo')} ({avisos.length})
        </h2>
      </div>
      <p className="mt-0.5 text-xs text-hce-muted">{t('sala.avisosCierre.instruccion')}</p>
      {mensajeError && (
        <Alert variant="red" className="mt-2">
          <AlertDescription>{mensajeError}</AlertDescription>
        </Alert>
      )}
      <ul className="mt-2 space-y-2">
        {avisos.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="min-w-[3.5rem] font-mono text-xs text-hce-muted">{a.turno ?? '—'}</span>
            <span className="min-w-[10rem] flex-1">
              <span className="font-medium text-hce-ink">{a.paciente}</span>
              <span className="ml-1.5 text-xs text-hce-muted">· {t('sala.avisosCierre.turnoDel', { fecha: fechaCortaEcuador(a.fechaTurno) })}</span>
            </span>
            <span className="flex flex-wrap gap-1.5">
              {a.encounterId && (
                <Button size="sm" variant="outline" aria-label={t('sala.accion.de', { accion: t('sala.avisosCierre.abrirFicha'), nombre: a.paciente })} onClick={() => void abrirHistoria(a)}>
                  {t('sala.avisosCierre.abrirFicha')}
                </Button>
              )}
              <Button
                size="sm"
                variant="default"
                disabled={ocupados.has(a.id)}
                aria-label={t('sala.accion.de', { accion: t('sala.avisosCierre.marcarRevisado'), nombre: a.paciente })}
                onClick={() => void revisar(a)}
              >
                {t('sala.avisosCierre.marcarRevisado')}
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
